import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { T, Var, useGT } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DiscountElementTargetType,
  DiscountElementType,
  DocumentAssociationType,
} from '@erp/shared-types';
import {
  CircleNotchIcon,
  FileTextIcon,
  UploadIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { FormattedInput } from '@/components/ui/formatted-input';
import { cn } from '@/lib/utils';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import {
  DISCOUNT_ATTACHMENT_ACCEPT,
  DISCOUNT_ATTACHMENT_HELP_TEXT,
  DISCOUNT_ATTACHMENT_MAX_SIZE_MB,
} from '@/api/accounts-receivable/discounts/attachmentConstants';
import useCustomer from 'src/api/accounts-receivable/customers/useCustomer';
import { TooltipInfoIcon } from 'src/components/common/TooltipInfoIcon';
import { formatCurrency } from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';

type SaveAs = 'FIXED_PRICE' | 'PERCENT' | 'AMOUNT_OFF';

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Per-line discount creation on the OMS v3 review page (Discounts v2 flavor of
 * the v1 CreateDiscountModal). Creates a single-element discount group scoped
 * to this line's variant + customer — approved immediately, since the sales
 * order itself is what goes through approval — and applies it to the line.
 */
export function CreateLineDiscountModal({
  open,
  onOpenChange,
  draftId,
  lineId,
  customerId,
  productVariantId,
  sku,
  poUnitPrice,
  listPrice,
  orderDate,
  currency,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftId: string;
  lineId: string;
  customerId: string;
  productVariantId: string;
  sku: string;
  poUnitPrice: number;
  listPrice: number | null;
  orderDate?: string | null;
  currency?: string | null;
}) {
  const t = useGT();
  const queryClient = useQueryClient();
  const uploadDocument = useUploadEntityDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: customerData } = useCustomer(customerId, {});
  const customerName: string = customerData?.customer?.name ?? '';

  const defaultName = customerName
    ? `${truncate(customerName, 30)} - ${sku} Discount`
    : `${sku} Discount`;
  // Null until the user edits — so the default tracks the async customer name.
  const [editedName, setEditedName] = useState<string | null>(null);
  const name = editedName ?? defaultName;

  const [saveAs, setSaveAs] = useState<SaveAs>('FIXED_PRICE');
  const [value, setValue] = useState<string>(poUnitPrice.toFixed(2));
  const [effectiveStart, setEffectiveStart] = useState(orderDate ?? todayIso());
  const [effectiveEnd, setEffectiveEnd] = useState(
    `${new Date().getFullYear()}-12-31`,
  );
  const [explanation, setExplanation] = useState('');
  const [maxQuantityInput, setMaxQuantityInput] = useState('');
  const [isSingleUse, setIsSingleUse] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const numericValue = Number.parseFloat(value);
  const isValueValid = Number.isFinite(numericValue) && numericValue >= 0;
  const hasListPrice = listPrice != null && listPrice > 0;

  /** The fixed unit price this form resolves to, whichever way it's saved. */
  const resolvedPrice =
    saveAs === 'FIXED_PRICE'
      ? numericValue
      : !hasListPrice || !isValueValid
        ? null
        : saveAs === 'PERCENT'
          ? listPrice * (1 - numericValue / 100)
          : listPrice - numericValue;

  /** Switch discount type, converting the value via the list price. */
  function switchMode(next: SaveAs) {
    if (next === saveAs) return;
    if (hasListPrice && isValueValid && resolvedPrice != null) {
      const converted =
        next === 'FIXED_PRICE'
          ? resolvedPrice
          : next === 'PERCENT'
            ? ((listPrice - resolvedPrice) / listPrice) * 100
            : listPrice - resolvedPrice;
      setValue(Math.max(0, converted).toFixed(2));
    }
    setSaveAs(next);
  }

  const blockers: string[] = [];
  if (name.trim().length === 0) blockers.push('Discount name is required.');
  if (!isValueValid) blockers.push('Enter a valid discount value.');
  if (
    saveAs === 'PERCENT' &&
    isValueValid &&
    (numericValue <= 0 || numericValue > 100)
  ) {
    blockers.push('Percentage must be between 0 and 100.');
  }
  if (
    saveAs === 'AMOUNT_OFF' &&
    isValueValid &&
    hasListPrice &&
    numericValue > listPrice
  ) {
    blockers.push('Amount off cannot exceed the list price.');
  }
  if (!effectiveStart || !effectiveEnd || effectiveEnd < effectiveStart) {
    blockers.push('End date must be on or after the start date.');
  }
  const trimmedMaxQuantity = maxQuantityInput.trim();
  if (
    trimmedMaxQuantity !== '' &&
    (!Number.isFinite(Number(trimmedMaxQuantity)) ||
      Number(trimmedMaxQuantity) <= 0)
  ) {
    blockers.push('Max quantity must be a positive number.');
  }
  const canSubmit = blockers.length === 0 && !submitting;

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    const maxBytes = DISCOUNT_ATTACHMENT_MAX_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadError(
        `File size must be less than ${DISCOUNT_ATTACHMENT_MAX_SIZE_MB}MB`,
      );
      return;
    }
    setUploadError(null);
    setAttachmentFile(file);
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Attachment first (pre-entity upload, price-list-dialog precedent): the
      // group stores the resulting documentId in its `attachment` field.
      let attachmentDocumentId: string | null = null;
      if (attachmentFile) {
        const uploaded = await uploadDocument.mutateAsync({
          entityType: 'ar_discount_group',
          entityId: crypto.randomUUID(),
          file: attachmentFile,
          associationType: DocumentAssociationType.DISCOUNT_ATTACHMENT,
        });
        attachmentDocumentId = uploaded.documentId;
      }

      const { discountGroupId } =
        await rpcClient.accountsReceivable.discountsV2.createGroup({
          name: name.trim(),
          description: explanation.trim() || null,
          customerId,
          attachment: attachmentDocumentId,
          elements: [
            {
              discountType:
                saveAs === 'FIXED_PRICE'
                  ? DiscountElementType.FIXED_PRICE
                  : saveAs === 'PERCENT'
                    ? DiscountElementType.PERCENT_BPS_OFF
                    : DiscountElementType.AMOUNT_OFF,
              // Percent crosses the wire as integer basis points.
              discountValue:
                saveAs === 'PERCENT'
                  ? Math.min(10000, Math.max(1, Math.round(numericValue * 100)))
                  : numericValue,
              targetType: DiscountElementTargetType.PRODUCT_VARIANT_ID,
              targetValue: productVariantId,
              effectiveStart,
              effectiveEnd,
              isSingleUse,
              maxQuantity:
                trimmedMaxQuantity === '' ? null : Number(trimmedMaxQuantity),
            },
          ],
          approveImmediately: true,
        });

      // Apply the created element to this line as its stack.
      const group = await rpcClient.accountsReceivable.discountsV2.getGroup({
        discountGroupId,
      });
      const element = group.elements[0];
      if (element) {
        await rpcClient.accountsReceivable.discountsV2.applyLineStack({
          draftSalesOrderId: draftId,
          draftLineId: lineId,
          elementIds: [element.id],
        });
      }

      toast.success(t('Discount created and applied'));
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.discountsV2.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.salesOrdersV3.get.queryKey({
          input: { id: draftId },
        }),
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Could not create the discount: ${err.message}`
          : 'Could not create the discount',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
          <DialogTitle>
            <T>Create Discount</T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description for creating a discount, showing sku and customer name">
              Create a new discount for <Var>{sku}</Var> and{' '}
              <Var>{customerName}</Var>
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="ld-name">
              <T>Discount Name</T>
            </Label>
            <Input
              id="ld-name"
              value={name}
              onChange={(e) => setEditedName(e.target.value)}
            />
          </div>

          {hasListPrice ? (
            <div className="space-y-3">
              <div>
                <Label>
                  <T>Discount type</T>
                </Label>
                <div className="mt-1 inline-flex rounded-md border p-0.5">
                  {(['FIXED_PRICE', 'PERCENT', 'AMOUNT_OFF'] as SaveAs[]).map(
                    (mode) => (
                      <button
                        key={mode}
                        type="button"
                        aria-pressed={saveAs === mode}
                        onClick={() => switchMode(mode)}
                        className={cn(
                          'rounded px-3 py-1 text-xs font-medium transition-colors',
                          saveAs === mode
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {mode === 'FIXED_PRICE' ? (
                          <T context="discount type toggle button for fixed price">
                            Fixed price
                          </T>
                        ) : mode === 'PERCENT' ? (
                          <T context="discount type toggle button for percentage off">
                            Percentage off
                          </T>
                        ) : (
                          <T context="discount type toggle button for a currency amount off">
                            Amount off
                          </T>
                        )}
                      </button>
                    ),
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label>
                    {saveAs === 'FIXED_PRICE' ? (
                      <T>Discounted Price ($)</T>
                    ) : saveAs === 'PERCENT' ? (
                      <T>Discount Off (%)</T>
                    ) : (
                      <T>Discount Off ($)</T>
                    )}
                  </Label>
                  <TooltipInfoIcon
                    ariaLabel={t('How the pricing field works')}
                    description={t(
                      'Enter the value for the chosen discount type — the resulting discounted price is computed from the list price.',
                    )}
                  />
                </div>
                <FormattedInput
                  formatType={saveAs === 'PERCENT' ? 'percentage' : 'currency'}
                  allowNegative={false}
                  value={value}
                  onChange={setValue}
                  placeholder={saveAs === 'PERCENT' ? '0' : '0.00'}
                  className="h-9"
                />
                <p className="text-xs text-muted-foreground">
                  <T context="label showing the list price value in the discount pricing section">
                    List price: <Var>{formatCurrency(listPrice, currency)}</Var>
                  </T>
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="ld-price">
                <T>Discounted Price ($)</T>
              </Label>
              <FormattedInput
                id="ld-price"
                formatType="currency"
                allowNegative={false}
                value={value}
                onChange={setValue}
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground">
                <T>
                  No list price on this line — the discount is saved as a fixed
                  price.
                </T>
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ld-start">
                <T>Effective Start</T>
              </Label>
              <Input
                id="ld-start"
                type="date"
                value={effectiveStart}
                onChange={(e) => setEffectiveStart(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ld-end">
                <T>Effective End</T>
              </Label>
              <Input
                id="ld-end"
                type="date"
                value={effectiveEnd}
                onChange={(e) => setEffectiveEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ld-reason">
              <T>Reason for Discount</T>
            </Label>
            <Textarea
              id="ld-reason"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder={t('Explain why this discount is being approved')}
              className="min-h-[80px] resize-none"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ld-max-qty" className="flex items-center gap-1">
              <T>Max quantity</T>
              <span className="text-xs font-normal text-muted-foreground">
                <T context="label suffix indicating the field is optional">
                  (optional)
                </T>
              </span>
              <TooltipInfoIcon
                ariaLabel={t('Max quantity help')}
                description={t(
                  'Total units this discount can ever apply to. Leave blank for unlimited.',
                )}
              />
            </Label>
            <Input
              id="ld-max-qty"
              type="number"
              min="0.0001"
              step="any"
              inputMode="decimal"
              value={maxQuantityInput}
              onChange={(e) => setMaxQuantityInput(e.target.value)}
              placeholder={t('Unlimited')}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={isSingleUse}
              onCheckedChange={(v) => setIsSingleUse(v === true)}
            />
            <span>
              <T context="checkbox label for making a discount single-use only">
                Single use
              </T>
            </span>
          </label>

          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <T context="label for the attachment upload section, indicating it is optional">
                Attachment (Optional)
              </T>
              <TooltipInfoIcon
                ariaLabel={t('Allowed attachment types')}
                description={`${t('Allowed:')} ${DISCOUNT_ATTACHMENT_HELP_TEXT} (${t('max')} ${DISCOUNT_ATTACHMENT_MAX_SIZE_MB}MB)`}
              />
            </Label>
            {attachmentFile ? (
              <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-md bg-muted/50 p-2">
                <FileTextIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm" title={attachmentFile.name}>
                  {attachmentFile.name}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => {
                    setAttachmentFile(null);
                    setUploadError(null);
                  }}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={DISCOUNT_ATTACHMENT_ACCEPT}
                  onChange={handleFileSelect}
                  className="hidden"
                  id="ld-attachment"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon className="mr-2 h-4 w-4" />
                  <T context="button label to upload a file attachment">
                    Upload File
                  </T>
                </Button>
              </div>
            )}
            {uploadError && (
              <p className="text-xs text-destructive">{uploadError}</p>
            )}
          </div>

          <div className="space-y-1 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            <div className="flex justify-between">
              <span>
                <T context="label in discount context info showing the customer name">
                  Customer:
                </T>
              </span>
              <span className="font-medium text-foreground">
                {customerName}
              </span>
            </div>
            <div className="flex justify-between">
              <span>
                <T context="label in discount context info showing the SKU">
                  SKU:
                </T>
              </span>
              <span className="font-mono text-foreground">{sku}</span>
            </div>
            <div className="flex justify-between">
              <span>
                <T context="label in discount context info showing the PO unit price">
                  PO Unit Price:
                </T>
              </span>
              <span className="text-foreground">
                {formatCurrency(poUnitPrice, currency)}
              </span>
            </div>
            {listPrice != null && (
              <div className="flex justify-between">
                <span>
                  <T context="label in discount context info showing the list price">
                    List Price:
                  </T>
                </span>
                <span className="text-foreground">
                  {formatCurrency(listPrice, currency)}
                </span>
              </div>
            )}
            {resolvedPrice != null && Number.isFinite(resolvedPrice) && (
              <div className="flex justify-between">
                <span>
                  <T context="label in discount context info showing the resulting unit price">
                    Discounted Price:
                  </T>
                </span>
                <span className="font-medium text-foreground">
                  {formatCurrency(resolvedPrice, currency)}
                </span>
              </div>
            )}
          </div>

          {blockers.length > 0 && (
            <ul className="list-inside list-disc text-xs text-muted-foreground">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            <T>Cancel</T>
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {submitting && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            <T context="button label to submit and create the discount">
              Create Discount
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
